import axios from "axios";

/**
 * QR Loyalty & Ordering — guest-facing API client.
 *
 * Deliberately NOT the app's shared axiosInstance: these endpoints are PUBLIC
 * (no auth), and the shared instance sends the httpOnly session cookie and
 * redirects to /login on 401/403 — wrong for a guest who is never logged in.
 * Same baseURL (…/api) so /loyalty/* hits /api/loyalty/*.
 */
const client = axios.create({
  baseURL: process.env.REACT_APP_NOT_SECRET_CODE,
  withCredentials: false,
  headers: { Accept: "application/json", "Content-Type": "application/json" },
});

/**
 * The guest page's one read on arrival.
 *
 * P5d — `entry` is the PHYSICAL-QR MARKER ("venue" for `?v=1`, "table" for
 * `?table={i}`) and it is what mints the `place_token` in the response. Omitted
 * for a bare `/v/{code}` — a bookmark, a shared link, the card's own button —
 * which comes back with no token and renders browse-only.
 */
export const getContext = (venueCode, entry) =>
  client
    .get(`/loyalty/context/${encodeURIComponent(venueCode)}`, {
      params: entry ? { entry } : undefined,
    })
    .then((r) => r.data);

export const postCheckin = (payload) =>
  client.post(`/loyalty/checkin`, payload).then((r) => r.data);

export const getBalance = (customerId) =>
  client.get(`/loyalty/balance/${encodeURIComponent(customerId)}`).then((r) => r.data);

/**
 * ONE featured item by id — the target of an order-first deep link
 * (`/v/{code}?item={id}`). Unlike the context list this is NOT filtered by the
 * item's daypart window: a guest photographs the screen QR while the dish is
 * live and opens it later, so the page shows the dish and uses `item.live` /
 * `item.available` to explain when it can't be ordered.
 *
 * P5d — always returns a `place_token`. Reaching this endpoint IS the presence
 * proof: it is the target of the QR on the venue's own screen, so unlike
 * getContext there is no bare form of it to tell apart.
 */
export const getItem = (venueCode, itemId) =>
  client
    .get(`/loyalty/item/${encodeURIComponent(venueCode)}/${encodeURIComponent(itemId)}`)
    .then((r) => r.data);

/**
 * Server-side recognition by phone — replaces the localStorage-only "known
 * guest", so the same person is recognised on a borrowed phone or after
 * clearing the browser. Read-only: it never creates a customer. Resolves to
 * { known:false, points:{pending} } for a number this venue hasn't seen.
 */
export const postLookup = (venueCode, phone) =>
  client.post(`/loyalty/lookup`, { venue_code: venueCode, phone }).then((r) => r.data);

/**
 * The customer card. The signed token IS the capability — bookmarkable and
 * cross-device, no cookie or login. View-only by construction: redemption lives
 * behind the cashier's till.
 *
 * Also the re-validation step for a cached session: a rotated or revoked token
 * 404s here, which is the signal to drop the cache and ask again.
 */
export const getCard = (token) =>
  client.get(`/loyalty/card/${encodeURIComponent(token)}`).then((r) => r.data);

/**
 * "I'm at the counter" — the card's doorbell (P5b).
 *
 * The ONLY write the card can make, and it spends nothing: it puts the guest's
 * name on the till board so a cashier can redeem for someone who ordered
 * nothing today. No points, no visit, no gate — the card stays view-only, which
 * is the whole reason a signed token in a URL is an acceptable identity.
 *
 * 422 `redemption_locked` = their first paid order hasn't happened yet;
 * 422 `nothing_affordable` = the balance moved and nothing is in reach.
 */
export const postAtCounter = (token, rewardId) =>
  client
    .post(`/loyalty/card/${encodeURIComponent(token)}/here`, rewardId ? { reward_id: rewardId } : {})
    .then((r) => r.data);

/** One ticket's live state — preparing → ready, and `paid` once the till closes it. */
export const getOrderStatus = (orderId) =>
  client.get(`/loyalty/order/${encodeURIComponent(orderId)}`).then((r) => r.data);

/**
 * Place a guest order for the venue's featured item.
 * payload: { venue_code, place_token, featured_item_id?, quantity?,
 *            table_number?|customer_name?, phone?|customer_id?, notes? }
 *
 * P5d — MUST carry `place_token`, the proof the guest came through a physical
 * QR in the venue. 422 `place_token_required` (missing, forged, or another
 * venue's) or `place_token_expired` (valid but aged out) — both mean the same
 * thing to the guest: scan the QR on the table or the screen. Identity is not
 * the issue in either case; the server knows exactly who they are.
 *
 * An order MUST carry an identity — `phone` (the order-first path, where the one
 * phone field on the order screen doubles as the check-in) or `customer_id` for a
 * guest already recognised this session. Without one the server refuses with 422
 * `identity_required`, because the response quotes points that could never be
 * credited to nobody. Prefer sending `phone`.
 */
export const postOrder = (payload) =>
  client.post(`/loyalty/orders`, payload).then((r) => r.data);

export default client;
